import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DocumentPreviewModal from '../../components/ui/DocumentPreviewModal';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import * as criminalAppellateService from '../../services/criminalAppellateCaseRequests';
import {
  getCriminalAppellateRequestViewData,
  saveCriminalAppellateRequest,
  type CriminalAppellateRequestViewData,
} from '../../services/criminalAppellateCaseRequests';
import type { CriminalAppellateWizardDraft } from '../../types/criminalAppellateCaseRequest';
import type { CriminalDocumentRow, CriminalWizardDraft } from '../../types/criminalCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import {
  CONTACT_NUMBER_LENGTH,
  contactNumberError,
  sliceMax,
  todayIsoDateLocal,
} from '../../utils/caseFieldValidation';
import {
  clearCriminalPreviewFile,
  setCriminalPreviewFile,
} from '../../utils/criminalDocumentPreviewCache';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { useCaseCategorization } from '../wizard/criminal/useCaseCategorization';
import {
  isHighUrgencyLevel,
  validateCriminalAppellateStep,
  type CriminalAppellateWizardStep,
} from '../wizard/criminalAppellate/criminalAppellateStepValidation';
import { useCriminalAppellateLookups } from '../wizard/criminalAppellate/useCriminalAppellateLookups';
import {
  deliveryMethodDisplay,
  submitterFromApiStep,
} from '../view-request/viewRequestHelpers';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

const JUDGEMENT_PREVIEW_ID = 'capp-upd-judgement-order';

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: CriminalAppellateRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

type EditableStep = 1 | 2 | 3;

function cloneDraft(d: CriminalAppellateWizardDraft): CriminalAppellateWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({ ...doc })),
  };
}

function draftHasUnsavedChanges(
  current: CriminalAppellateWizardDraft,
  saved: CriminalAppellateWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: CriminalAppellateWizardDraft) {
  return {
    description: d.description,
    appealMatter: d.appealMatter,
    originalCourt: d.originalCourt,
    judgementOrderFileName: d.judgementOrderFileName,
    judgementOrderOriginalFileName: d.judgementOrderOriginalFileName,
    provinceId: d.provinceId,
    districtId: d.districtId,
    policeStationId: d.policeStationId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRefAvailable: d.connectedAgdFileRefAvailable,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: CriminalAppellateWizardDraft) {
  return {
    plaintiffApplicantName: d.plaintiffApplicantName,
    defendantRespondentName: d.defendantRespondentName,
    clientType: d.clientType,
    otherInvolvedInstitutions: d.otherInvolvedInstitutions,
    legalOfficerName: d.legalOfficerName,
    legalOfficerContact: d.legalOfficerContact,
  };
}

function pickStep3Snapshot(d: CriminalAppellateWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: EditableStep, d: CriminalAppellateWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.description.trim() ||
      d.yourReferenceNumber.trim() ||
      d.provinceId.trim() ||
      d.caseNumber.trim() ||
      d.appealMatter
    );
  }
  if (step === 2) {
    return !!(
      d.plaintiffApplicantName.trim() ||
      d.defendantRespondentName.trim() ||
      d.legalOfficerName.trim() ||
      d.legalOfficerContact.trim()
    );
  }
  return !!(d.urgencyLevelId.trim() || d.deadline.trim() || d.urgencyJustification.trim());
}

function canSaveStep(
  step: EditableStep,
  draft: CriminalAppellateWizardDraft | null,
  initial: CriminalAppellateWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick = step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

export default function TaskCaseCriminalAppellateUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<CriminalAppellateWizardDraft | null>(null);
  const [initial, setInitial] = useState<CriminalAppellateWizardDraft | null>(null);
  const [step4, setStep4] = useState<CriminalSaveStep4 | undefined>();
  const [coverSubmitter, setCoverSubmitter] =
    useState<CriminalAppellateRequestViewData['coverSubmitter']>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<CriminalAppellateWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingJudgement, setUploadingJudgement] = useState(false);
  const [judgementPreviewOpen, setJudgementPreviewOpen] = useState(false);
  const judgementFileInputRef = useRef<HTMLInputElement>(null);
  const isSaving = savingStep !== null;

  const lookups = useCriminalAppellateLookups(
    'review',
    draft?.provinceId ?? '',
    draft?.districtId ?? '',
  );
  const cat = useCaseCategorization(draft?.fileCategoryId ?? '', draft?.subFileCategoryId ?? '');
  const minDeadlineDate = useMemo(() => todayIsoDateLocal(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep4(undefined);
          setCoverSubmitter(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep4(viewData.step4);
        setCoverSubmitter(viewData.coverSubmitter);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getCriminalAppellateRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep4(undefined);
        setCoverSubmitter(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep4(res.data.step4);
      setCoverSubmitter(res.data.coverSubmitter);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const canSaveStep3 = useMemo(() => canSaveStep(3, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const handleJudgementUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (judgementFileInputRef.current) judgementFileInputRef.current.value = '';
    if (!file || readOnly) return;
    setUploadingJudgement(true);
    const result = await criminalAppellateService.uploadCaseFile(file);
    setUploadingJudgement(false);
    if (!result.success) {
      setFlashToast({ message: result.message ?? 'Upload failed.', variant: 'error' });
      return;
    }
    setCriminalPreviewFile(JUDGEMENT_PREVIEW_ID, file);
    setDraft((d) =>
      d
        ? {
            ...d,
            judgementOrderFileName: result.data.file_name,
            judgementOrderOriginalFileName: result.data.original_file_name ?? file.name,
            judgementOrderFileSize: result.data.file_size,
          }
        : d,
    );
    clearErr('judgementOrderFileName');
  };

  const removeJudgement = () => {
    clearCriminalPreviewFile(JUDGEMENT_PREVIEW_ID);
    setJudgementPreviewOpen(false);
    setDraft((d) =>
      d
        ? {
            ...d,
            judgementOrderFileName: '',
            judgementOrderOriginalFileName: '',
            judgementOrderFileSize: undefined,
          }
        : d,
    );
  };

  const saveSection = async (step: EditableStep) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const result = validateCriminalAppellateStep(
      step,
      draft,
      cat.fileCategories,
      lookups.corporateInstitutes,
    );
    const errors = result.errors;
    const specs = result.specs;
    setErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstInvalid(specs, errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveCriminalAppellateRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pick =
          step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
        return mergeSavedStepIntoInitialBaseline(prev, next, pick, cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const fromApi = submitterFromApiStep(step4);
  const submitter = {
    agency: fromApi.agency !== '—' ? fromApi.agency : coverSubmitter?.agency?.trim() || '—',
    division: fromApi.division !== '—' ? fromApi.division : coverSubmitter?.division?.trim() || '—',
    designation: fromApi.designation,
    name: fromApi.name,
    email: fromApi.email,
    contact: fromApi.contact,
  };
  const deliveryLabel = deliveryMethodDisplay(
    draft as unknown as CriminalWizardDraft,
    lookups.deliveryMethods,
    step4,
  );
  const instituteLabel =
    draft.instituteType === 'corporation'
      ? draft.corporateInstituteOther || draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
              <ReviewField label="Institute Type" value={instituteLabel} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Intake Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Description <span className="req">*</span>
              </label>
              <textarea
                id="capp-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fg" id="capp-appealMatter" style={{ marginBottom: 14 }}>
              <label>
                Appeal Matter <span className="req">*</span>
              </label>
              <div className="ccr-radio-row">
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="capp-appealMatter"
                      checked={draft.appealMatter === v}
                      disabled={readOnly}
                      onChange={() => {
                        if (v === 'no') {
                          clearCriminalPreviewFile(JUDGEMENT_PREVIEW_ID);
                          setJudgementPreviewOpen(false);
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  appealMatter: 'no',
                                  originalCourt: '',
                                  judgementOrderFileName: '',
                                  judgementOrderOriginalFileName: '',
                                  judgementOrderFileSize: undefined,
                                }
                              : d,
                          );
                          clearErr('originalCourt');
                          clearErr('judgementOrderFileName');
                        } else {
                          setDraft((d) => (d ? { ...d, appealMatter: 'yes' } : d));
                        }
                        clearErr('appealMatter');
                      }}
                    />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
              <FieldError message={errors.appealMatter} />
            </div>

            {draft.appealMatter === 'yes' ? (
              <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
                <div className="ccr-fg">
                  <label>
                    Original Court <span className="req">*</span>
                  </label>
                  <input
                    id="capp-originalCourt"
                    className={inpClass(Boolean(errors.originalCourt))}
                    value={draft.originalCourt}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_REF}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, originalCourt: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('originalCourt');
                    }}
                  />
                  <FieldError message={errors.originalCourt} />
                </div>
                <div className="ccr-fg" id="capp-judgementOrder">
                  <label>
                    Judgement / Order <span className="req">*</span>
                  </label>
                  {readOnly ? (
                    <ReviewField
                      label=""
                      value={
                        draft.judgementOrderOriginalFileName ||
                        draft.judgementOrderFileName ||
                        '—'
                      }
                    />
                  ) : draft.judgementOrderFileName ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, wordBreak: 'break-all' }}>
                        {draft.judgementOrderOriginalFileName || draft.judgementOrderFileName}
                      </span>
                      <button
                        className="ccr-btn-ol"
                        type="button"
                        onClick={() => setJudgementPreviewOpen(true)}
                        aria-label="View Judgement / Order"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <EyeIcon />
                        View
                      </button>
                      <button
                        className="ccr-btn-ol"
                        type="button"
                        onClick={removeJudgement}
                        aria-label="Remove Judgement / Order"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TrashIcon />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      className="ccr-btn-ol"
                      type="button"
                      disabled={uploadingJudgement}
                      onClick={() => judgementFileInputRef.current?.click()}
                    >
                      {uploadingJudgement ? 'Uploading…' : 'Upload'}
                    </button>
                  )}
                  {!readOnly ? (
                    <input
                      ref={judgementFileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.xlsx,.docx"
                      style={{ display: 'none' }}
                      onChange={(e) => void handleJudgementUpload(e)}
                    />
                  ) : null}
                  <FieldError message={errors.judgementOrderFileName} />
                </div>
              </div>
            ) : null}

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Province <span className="req">*</span>
                </label>
                <WizardSelect
                  id="capp-province"
                  hasError={Boolean(errors.provinceId)}
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={toSelectOpts(lookups.provinces)}
                  onChange={(id) => {
                    const name = lookups.provinces.find((p) => p.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            provinceId: id,
                            provinceName: name,
                            districtId: '',
                            districtName: '',
                            policeStationId: '',
                            policeStationName: '',
                          }
                        : d,
                    );
                    clearErr('provinceId');
                  }}
                />
                <FieldError message={errors.provinceId} />
              </div>
              <div className="ccr-fg">
                <label>
                  District <span className="req">*</span>
                </label>
                <WizardSelect
                  id="capp-district"
                  hasError={Boolean(errors.districtId)}
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={toSelectOpts(lookups.districts)}
                  onChange={(id) => {
                    const name = lookups.districts.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            districtId: id,
                            districtName: name,
                            policeStationId: '',
                            policeStationName: '',
                          }
                        : d,
                    );
                    clearErr('districtId');
                  }}
                />
                <FieldError message={errors.districtId} />
              </div>
              <div className="ccr-fg">
                <label>Police Station</label>
                <WizardSelect
                  value={draft.policeStationId}
                  disabled={readOnly || !draft.districtId}
                  placeholder={draft.districtId ? 'Select Police Station' : 'Select district first'}
                  options={toSelectOpts(lookups.policeStations)}
                  onChange={(id) => {
                    const name = lookups.policeStations.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) =>
                      d ? { ...d, policeStationId: id, policeStationName: name } : d,
                    );
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>
                  Your Reference Number (External) <span className="req">*</span>
                </label>
                <input
                  id="capp-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(false)}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>Case Number</label>
                <input
                  className={inpClass(false)}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d))
                  }
                />
              </div>
              <div className="ccr-fg" id="capp-connected">
                <label>
                  Are there any connected matters (Cases)? <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="capp-connected"
                        checked={draft.hasConnectedMatters === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedMatters: v,
                                  ...(v === 'no'
                                    ? {
                                        connectedCaseNumber: '',
                                        connectedAgdFileRefAvailable: false,
                                        connectedAgdFileRef: '',
                                      }
                                    : {}),
                                }
                              : d,
                          );
                          clearErr('hasConnectedMatters');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedMatters} />
              </div>
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      Connected Case Number <span className="req">*</span>
                    </label>
                    <input
                      id="capp-connectedCase"
                      className={inpClass(Boolean(errors.connectedCaseNumber))}
                      value={draft.connectedCaseNumber}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedCaseNumber');
                      }}
                    />
                    <FieldError message={errors.connectedCaseNumber} />
                  </div>
                  <div className="ccr-fg">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <label style={{ marginBottom: 0 }}>
                        Connected AGD File Reference Number{' '}
                        {draft.connectedAgdFileRefAvailable ? <span className="req">*</span> : null}
                      </label>
                      <input
                        type="checkbox"
                        style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer' }}
                        checked={draft.connectedAgdFileRefAvailable}
                        disabled={readOnly}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  connectedAgdFileRefAvailable: checked,
                                  connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                                }
                              : d,
                          );
                          clearErr('connectedAgdFileRef');
                        }}
                      />
                      <span>Available</span>
                    </div>
                    <input
                      id="capp-connectedAgd"
                      className={inpClass(Boolean(errors.connectedAgdFileRef))}
                      value={draft.connectedAgdFileRef}
                      readOnly={readOnly || !draft.connectedAgdFileRefAvailable}
                      disabled={readOnly || !draft.connectedAgdFileRefAvailable}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <FieldError message={errors.connectedAgdFileRef} />
                  </div>
                </>
              ) : null}
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Party Details">
          <ReviewSubBlock title="Party Details">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Plaintiff / Applicant Name <span className="req">*</span>
                </label>
                <input
                  id="capp-plaintiff"
                  className={inpClass(Boolean(errors.plaintiffApplicantName))}
                  value={draft.plaintiffApplicantName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, plaintiffApplicantName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('plaintiffApplicantName');
                  }}
                />
                <FieldError message={errors.plaintiffApplicantName} />
              </div>
              <div className="ccr-fg">
                <label>
                  Defendant / Respondent Name <span className="req">*</span>
                </label>
                <input
                  id="capp-defendant"
                  className={inpClass(Boolean(errors.defendantRespondentName))}
                  value={draft.defendantRespondentName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, defendantRespondentName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('defendantRespondentName');
                  }}
                />
                <FieldError message={errors.defendantRespondentName} />
              </div>
            </div>

            <div className="ccr-fg" id="capp-clientType" style={{ marginBottom: 14 }}>
              <label>
                Select one of the following <span className="req">*</span>
              </label>
              <div className="ccr-radio-row">
                <label className="ccr-radio">
                  <input
                    type="radio"
                    name="capp-clientType"
                    checked={draft.clientType === 'government'}
                    disabled={readOnly}
                    onChange={() => {
                      setDraft((d) => (d ? { ...d, clientType: 'government' } : d));
                      clearErr('clientType');
                    }}
                  />
                  Government Ministry / Department / Other Unincorporated Body
                </label>
                <label className="ccr-radio">
                  <input
                    type="radio"
                    name="capp-clientType"
                    checked={draft.clientType === 'corporation'}
                    disabled={readOnly}
                    onChange={() => {
                      setDraft((d) => (d ? { ...d, clientType: 'corporation' } : d));
                      clearErr('clientType');
                    }}
                  />
                  Public Corporation / Statutory Board / State Owned Entity / Other Incorporated Body
                </label>
              </div>
              <FieldError message={errors.clientType} />
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Other Involved Institutions</label>
              <textarea
                className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.otherInvolvedInstitutions}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, otherInvolvedInstitutions: sliceMax(e.target.value, MAX_DESC) } : d,
                  )
                }
              />
              <FieldError message={errors.otherInvolvedInstitutions} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Name of the Legal Officer / Officer In-Charge <span className="req">*</span>
                </label>
                <input
                  id="capp-legalOfficer"
                  className={inpClass(Boolean(errors.legalOfficerName))}
                  value={draft.legalOfficerName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, legalOfficerName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('legalOfficerName');
                  }}
                />
                <FieldError message={errors.legalOfficerName} />
              </div>
              <div className="ccr-fg">
                <label>
                  Contact Number <span className="req">*</span>
                </label>
                <input
                  id="capp-legalContact"
                  className={inpClass(Boolean(errors.legalOfficerContact))}
                  inputMode="numeric"
                  value={draft.legalOfficerContact}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_CONTACT}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                    setDraft((d) => (d ? { ...d, legalOfficerContact: digits } : d));
                    const err = contactNumberError(digits);
                    setErrors((prev) => ({ ...prev, legalOfficerContact: err ?? '' }));
                  }}
                />
                <FieldError message={errors.legalOfficerContact} />
              </div>
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Timeline">
          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="capp-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="capp-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification{' '}
                {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="capp-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep3}
            saving={savingStep === 3}
            readOnly={readOnly}
            onSave={() => void saveSection(3)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Agency Name" value={submitter.agency} />
              <ReviewField label="Submitted Division/Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery Method">
            <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
            {draft.deliveryOther.trim() ? (
              <ReviewField label="Other Delivery Method" value={draft.deliveryOther} />
            ) : null}
          </ReviewSubBlock>
        </ReviewSection>
      </div>

      <DocumentPreviewModal
        isOpen={judgementPreviewOpen}
        doc={
          {
            id: JUDGEMENT_PREVIEW_ID,
            documentName: 'Judgement / Order',
            fileName: draft.judgementOrderFileName,
            originalFileName: draft.judgementOrderOriginalFileName,
          } as unknown as CriminalDocumentRow
        }
        onClose={() => setJudgementPreviewOpen(false)}
      />
    </>
  );
}
